import React, { memo } from "react";

import { ChangeHandler, useFormContext } from "react-hook-form";

import { Box, Heading, Stack, Text } from "@origin-digital/ods-core";

import {
  RadioGroupInput,
  RouterActions,
  StyledHeading,
  StyledText,
  TextInput,
} from "@/components";
import { StyledQuestion } from "@/components/StyledQuestion";
import { useFieldValidation } from "@/hooks";
import { PageBaseProps } from "@/types/SolarFormProps";

import { averageBill } from "./content.json";
import { transformCurrency } from "./utils/transformCurrency";

type AverageBillProps = PageBaseProps & {
  nextGoInstallerRoute: () => Promise<string>;
};

export const AverageBill = memo(
  ({ backRoute, nextRoute, nextGoInstallerRoute }: AverageBillProps) => {
    const { register, setValue } = useFormContext();
    const { isValid } = useFieldValidation({
      fields: ["averageBill", "billDuration"],
    });

    const handleOnBlur: ChangeHandler = (e) =>
      Promise.resolve(
        setValue("averageBill", transformCurrency(e.target.value))
      );

    const handleGoInstallerRoute = () => {
      setValue("isKipAndGoInstaller", true);
      setValue("degradedExperience", true);
      return nextGoInstallerRoute();
    };

    return (
      <StyledQuestion>
        <StyledHeading>{averageBill.heading}</StyledHeading>
        <Stack space={"small"}>
          <StyledText>{averageBill.description}</StyledText>
          <TextInput
            {...register("averageBill")}
            id={averageBill.input.id}
            data-id={averageBill.input.id}
            label={averageBill.input.label}
            onBlur={handleOnBlur}
          />
          <Stack space="small">
            <Text>{averageBill.duration.label}</Text>
            <RadioGroupInput
              data-id={averageBill.duration.id}
              name="billDuration"
              label=""
              options={averageBill.duration.option}
              direction={"row"}
            />
          </Stack>
        </Stack>

        <Box marginTop="xxlarge">
          <Heading variant="h4" tone="neutralDark">
            {averageBill.talkToInstaller.heading}
          </Heading>
          <StyledText>{averageBill.talkToInstaller.description}</StyledText>
          <RouterActions
            nextLabel={averageBill.talkToInstaller.label}
            onNextRoute={handleGoInstallerRoute}
            isIconEnd={true}
            onNext={() => Promise.resolve(true)}
          />
        </Box>

        <RouterActions
          onBackRoute={backRoute}
          onNextRoute={nextRoute}
          onNext={isValid}
        />
      </StyledQuestion>
    );
  }
);
